import { Button, Checkbox, Input, Label, Textarea } from '@invent/wl-ui-kit-next/ui';
import * as React from 'react';

import { ApiError, ProblemType } from '@/domains/api';
import type { IPermissionInfo } from '../../api/role-contracts';
import { groupCatalogue, type usePermissionCatalogue } from '../../components/account-roles';
import { LoadError } from '../../components/admin-page';
import {
  ROLES_FORBIDDEN,
  namesSomethingGone,
  neverArrived,
  refusalMessage,
  roleModelRefusal
} from '../../refusal';

/**
 * A Team Portal role's fields — code (on create only), name, description, and its permissions
 * grouped as `GET /admin/permissions` groups them, each a box with the catalogue's description
 * (the role-model UI design's §7). One form for New role and a role's edit.
 *
 * **Nothing is judged here.** A permission the editor does not hold is drawn and its box is
 * enabled — whether it may be given is survey-api's D5, said from `permission-not-held`; the code's
 * pattern and whether a code or name is taken are survey-api's too, said at the box they name. A
 * permission the role holds and the catalogue lacks is drawn under "Not in the catalogue", ticked,
 * so a save never drops it unseen.
 */

export interface IRoleDraft {
  code: string;
  name: string;
  description: string;
  permissions: readonly string[];
}

/** The members a refusal is placed at; `form` is everything no member names. */
export type RoleField = 'code' | 'name' | 'description' | 'permissions' | 'form';

export type RoleProblems = Partial<Record<RoleField, string[]>>;

/** The box a problem's `target` names — `code`, `$.code`, `permissions[3]` — or the form. */
function fieldOf(target: string): RoleField {
  const head = target.replace(/^\$\.?/, '').split(/[.[]/)[0]?.toLowerCase() ?? '';
  return head === 'code' || head === 'name' || head === 'description' || head === 'permissions'
    ? head
    : 'form';
}

/** What a write's answer says, and what the page does about it. */
export interface IRoleRefusal {
  problems: RoleProblems;
  /** 409 `revision-conflict`: the draft is kept, and Reload offered. */
  conflict?: boolean;
  /**
   * The role is gone (404), the answer never arrived, or the form offered a permission survey-api
   * no longer knows: the page reads the role and the catalogue again.
   */
  reread?: boolean;
}

/**
 * A create's, an edit's or a delete's refusal, told by status and `type`, never by `title`: the
 * role model's 422s in `roleModelRefusal`'s words, a 400's `problems[]` at the boxes they name,
 * a taken code or name at its box, and the rest under the form. `what` completes "the answer to
 * …" and "Could not …".
 */
export function roleRefusal(error: unknown, what: string): IRoleRefusal {
  if (!(error instanceof ApiError) || error.status >= 500) {
    return {
      problems: {
        form: [
          neverArrived(what, 'The roles are read again — check them before trying again.')
        ]
      },
      reread: true
    };
  }
  const modelled = roleModelRefusal(error);
  if (modelled) {
    return { problems: { form: [modelled] }, reread: namesSomethingGone(error) || undefined };
  }
  if (error.isProblem(ProblemType.revisionConflict)) {
    return {
      problems: {
        form: [
          'Someone saved this role after you opened it. Your changes are kept here; Reload reads ' +
            'theirs in their place.'
        ]
      },
      conflict: true
    };
  }
  // One type for a taken code and a taken name — survey-api's `code-in-use`, its one problem's
  // `target` naming which (§9, 6 as built).
  if (error.isProblem(ProblemType.codeInUse)) {
    return error.problem?.problems?.[0]?.target === 'name'
      ? { problems: { name: ['Another role already has this name.'] } }
      : { problems: { code: ['Another role already has this code.'] } };
  }
  if (error.status === 400 && error.problem?.problems?.length) {
    const problems: RoleProblems = {};
    for (const problem of error.problem.problems) {
      const field = fieldOf(problem.target);
      problems[field] = [...(problems[field] ?? []), problem.reason];
    }
    return { problems };
  }
  if (error.status === 403) return { problems: { form: [ROLES_FORBIDDEN] } };
  if (error.status === 404) {
    return {
      problems: { form: ['This role no longer exists — it was deleted after the page read it.'] },
      reread: true
    };
  }
  if (error.status === 428) {
    return {
      problems: {
        form: [
          'This page sent the change without the role’s revision — the page’s fault, not yours. ' +
            'Reload the role and make the change again.'
        ]
      }
    };
  }
  return { problems: { form: [refusalMessage(error, `Could not ${what}`)] } };
}

/** Said beside a box, as survey-api said it. */
export function ProblemLines({ id, lines }: { id?: string; lines?: string[] }) {
  if (!lines || lines.length === 0) return null;
  return (
    <div id={id} role="alert" className="grid gap-0.5 text-sm text-destructive">
      {lines.map((line, index) => (
        <span key={index}>{line}</span>
      ))}
    </div>
  );
}

export function RoleFields({
  draft,
  onDraft,
  catalogue,
  problems,
  codeEditable,
  disabled
}: {
  draft: IRoleDraft;
  onDraft: (next: IRoleDraft) => void;
  catalogue: readonly IPermissionInfo[];
  problems: RoleProblems;
  /** On create; an edit says the code is fixed instead, and sends none. */
  codeEditable: boolean;
  disabled: boolean;
}) {
  const id = React.useId();
  const groups = groupCatalogue(catalogue);
  const unknown = draft.permissions.filter(
    (code) => !catalogue.some((entry) => entry.code === code)
  );
  const toggle = (code: string, on: boolean) =>
    onDraft({
      ...draft,
      permissions: on
        ? [...draft.permissions, code]
        : draft.permissions.filter((candidate) => candidate !== code)
    });

  const box = (code: string, description: React.ReactNode) => (
    <div key={code} className="flex items-start gap-2 text-sm" data-permission={code}>
      <Checkbox
        id={`${id}-${code}`}
        checked={draft.permissions.includes(code)}
        disabled={disabled}
        onCheckedChange={(next) => toggle(code, next === true)}
      />
      <label htmlFor={`${id}-${code}`} className="grid gap-0.5">
        <span>{description}</span>
        <code className="text-xs text-muted-foreground">{code}</code>
      </label>
    </div>
  );

  return (
    <div className="grid gap-4">
      {codeEditable && (
        <div className="grid gap-1.5">
          <Label htmlFor={`${id}-code`}>Code</Label>
          <Input
            id={`${id}-code`}
            value={draft.code}
            onChange={(event) => onDraft({ ...draft, code: event.target.value })}
            disabled={disabled}
            aria-describedby={`${id}-code-help ${id}-code-problems`}
            className="max-w-sm font-mono"
            autoComplete="off"
          />
          <span id={`${id}-code-help`} className="text-xs text-muted-foreground">
            Lowercase letters, digits and underscores, starting with a letter — fixed once the role
            is made.
          </span>
          <ProblemLines id={`${id}-code-problems`} lines={problems.code} />
        </div>
      )}
      <div className="grid gap-1.5">
        <Label htmlFor={`${id}-name`}>Name</Label>
        <Input
          id={`${id}-name`}
          value={draft.name}
          onChange={(event) => onDraft({ ...draft, name: event.target.value })}
          disabled={disabled}
          aria-describedby={`${id}-name-problems`}
          className="max-w-sm"
          autoComplete="off"
        />
        <ProblemLines id={`${id}-name-problems`} lines={problems.name} />
      </div>
      <div className="grid gap-1.5">
        <Label htmlFor={`${id}-description`}>Description</Label>
        <Textarea
          id={`${id}-description`}
          value={draft.description}
          onChange={(event) => onDraft({ ...draft, description: event.target.value })}
          disabled={disabled}
          aria-describedby={`${id}-description-problems`}
          rows={2}
        />
        <ProblemLines id={`${id}-description-problems`} lines={problems.description} />
      </div>
      <fieldset className="grid gap-3" aria-describedby={`${id}-permissions-problems`}>
        <legend className="mb-1 text-sm font-medium">Permissions</legend>
        {groups.map((group) => (
          <div key={group.group} className="grid gap-2" data-permission-group={group.group}>
            <span className="text-xs tracking-wide text-muted-foreground uppercase">
              {group.label}
            </span>
            {group.entries.map((entry) => box(entry.code, entry.description))}
          </div>
        ))}
        {unknown.length > 0 && (
          <div className="grid gap-2">
            <span className="text-xs tracking-wide text-muted-foreground uppercase">
              Not in the catalogue
            </span>
            {unknown.map((code) => box(code, 'A permission the catalogue does not list'))}
          </div>
        )}
        <ProblemLines id={`${id}-permissions-problems`} lines={problems.permissions} />
      </fieldset>
      <ProblemLines lines={problems.form} />
    </div>
  );
}

/**
 * The permission catalogue's three arms around what needs it: "Reading…", its failure with "Read
 * again", or the form over it. A role's permissions are boxes only the catalogue can label and
 * group, so nothing is offered to tick until it is read.
 */
export function CatalogueGate({
  catalogue,
  children
}: {
  catalogue: ReturnType<typeof usePermissionCatalogue>;
  children: (permissions: IPermissionInfo[]) => React.ReactNode;
}) {
  const permissions = catalogue.data?.permissions ?? null;
  if (permissions) return <>{children(permissions)}</>;
  if (catalogue.error) {
    return (
      <div className="grid justify-items-start gap-2">
        <LoadError message={catalogue.error} />
        <Button type="button" variant="outline" size="sm" onClick={() => void catalogue.reload()}>
          Read again
        </Button>
      </div>
    );
  }
  return <p className="text-sm text-muted-foreground">Reading the permission catalogue…</p>;
}

/** The body's members from a draft: a blank description is sent as none. */
export function roleBody(draft: IRoleDraft) {
  const description = draft.description.trim();
  return {
    name: draft.name,
    ...(description === '' ? {} : { description: draft.description }),
    permissions: [...draft.permissions]
  };
}

/** Whether two drafts would send the same body — the no-op an unchanged Save is. */
export function sameDraft(a: IRoleDraft, b: IRoleDraft): boolean {
  return (
    a.name === b.name &&
    a.description.trim() === b.description.trim() &&
    a.permissions.length === b.permissions.length &&
    a.permissions.every((code) => b.permissions.includes(code))
  );
}
